/**
 * DropPreview — the structural preview of what you're about to drop, shared by
 * the palette tiles and the drag ghost. It's a single <img> of an SVG drawing
 * (see svgPreview): a section shows its widget layout, a widget shows itself, a
 * page shows a nav band over its stacked sections.
 *
 * An <img> (not live DOM) so the same drawing rasterizes once and is reused
 * across every tile + the ghost — see svgPreview for the perf rationale.
 */
import { html, css } from "react-strict-dom";
import { colors } from "@duro-app/tokens/tokens/colors.css";
import { radii } from "@duro-app/tokens/tokens/spacing.css";
import { borders } from "@duro-app/tokens/tokens/borders.css";

import {
  sectionPreview,
  widgetPreview,
  dsWidgetPreview,
  pagePreview,
  type PreviewSvg,
} from "./svgPreview.js";

const styles = css.create({
  img: { display: "block" },
  // Dynamic: the crop tile's height/width vary per call (StyleX dynamic style).
  crop: (w: number, h: number) => ({ width: w, height: h }),
  cropFrame: {
    overflow: "hidden",
    borderRadius: radii.xs,
    borderWidth: borders.hairline,
    borderStyle: "solid",
    borderColor: colors.border,
    // The page ground, so any preview shorter than the crop shows it (not the
    // canvas) below it, rather than a transparent gap.
    backgroundColor: colors.bg,
  },
});

export interface DropPreviewProps {
  readonly kind: "section" | "page" | "widget";
  readonly templateId: string;
  readonly widgetType?: string;
  readonly label: string;
  /** Render width; omit for the drawing's natural width (the drag ghost). */
  readonly width?: number;
  /** When set, crop the (variable-height) preview to a uniform tile height. */
  readonly cropHeight?: number;
}

function svgFor(
  kind: DropPreviewProps["kind"],
  templateId: string,
  widgetType: string | undefined,
  tile: boolean,
): PreviewSvg {
  if (kind === "widget") {
    const ds = dsWidgetPreview(templateId);
    if (ds) return ds;
    if (widgetType) return widgetPreview(widgetType);
  }
  if (kind === "page") return pagePreview(templateId);
  return sectionPreview(templateId, tile);
}

export function DropPreview({
  kind,
  templateId,
  widgetType,
  label,
  width,
  cropHeight,
}: DropPreviewProps) {
  // Cropped (cropHeight set) = a palette tile → use the uniform-height section
  // preview so every tile crops to the same window.
  const svg = svgFor(kind, templateId, widgetType, cropHeight != null);
  const w = width ?? svg.w;
  const h = (svg.h / svg.w) * w;

  const img = (
    <html.img
      src={svg.uri}
      alt={label}
      width={w}
      height={h}
      draggable={false}
      style={styles.img}
    />
  );

  if (cropHeight == null) return img;
  return (
    <html.div style={[styles.cropFrame, styles.crop(w, cropHeight)]}>
      {img}
    </html.div>
  );
}
